'use client';
import React, { useEffect, useState } from 'react';
import { getAthleteWellnessHistory, calculateStreak } from '@/lib/wellnessService';
import type { WellnessCheckinRow } from '@/lib/wellnessService';
import { STATUS_META } from '@/lib/wellness';
import type { ReadinessStatus } from '@/lib/wellness';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine
} from 'recharts';
import { Flame, TrendingUp, AlertTriangle, Calendar, Moon, Activity, Brain } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface AthleteWellnessPanelProps {
  athleteId: string;
  todayCheckin: WellnessCheckinRow | null;
}

function ReadinessBadge({ status, score }: { status: ReadinessStatus; score: number }) {
  const meta = STATUS_META[status];
  return (
    <div
      className="rounded-2xl border p-6 flex flex-col items-center gap-2"
      style={{ background: meta.bg, borderColor: meta.border }}
    >
      <p className="text-xs font-medium uppercase tracking-widest" style={{ color: meta.color }}>
        Daily Readiness Score
      </p>
      <p className="text-6xl font-black" style={{ color: meta.color }}>{score}</p>
      <span
        className="px-3 py-1 rounded-full text-xs font-semibold border"
        style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}
      >
        {meta.label}
      </span>
    </div>
  );
}

const STATUS_SCORE_LINES: Record<ReadinessStatus, number> = {
  green: 85, yellow: 70, orange: 50, red: 0,
};

export default function AthleteWellnessPanel({ athleteId, todayCheckin }: AthleteWellnessPanelProps) {
  const [history, setHistory] = useState<WellnessCheckinRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!athleteId) return;
    getAthleteWellnessHistory(athleteId, 30).then(({ data }) => {
      setHistory(data);
      setLoading(false);
    });
  }, [athleteId]);

  const streak = calculateStreak(history);

  const chartData = history.map(r => ({
    date: new Date(r.checkin_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    readiness: r.readiness_score,
    sleep: r.sleep_duration,
    soreness: r.soreness_level,
    stress: r.stress_level,
    status: r.readiness_status,
  }));

  if (!todayCheckin) {
    return (
      <div className="card p-6 flex flex-col items-center text-center gap-3">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(225,6,0,0.1)', border: '1px solid rgba(225,6,0,0.2)' }}
        >
          <Calendar size={22} style={{ color: '#E10600' }} />
        </div>
        <p className="text-base font-semibold text-zinc-200">No Check-In Today</p>
        <p className="text-sm text-zinc-500">Complete your daily wellness check-in to see your readiness score and training recommendation.</p>
      </div>
    );
  }

  const meta = STATUS_META[todayCheckin.readiness_status as ReadinessStatus];
  const hasPain = todayCheckin.body_pain;

  return (
    <div className="space-y-4">
      {/* Today's Readiness */}
      <ReadinessBadge status={todayCheckin.readiness_status as ReadinessStatus} score={todayCheckin.readiness_score} />

      {/* Pain Alert */}
      {hasPain && (
        <div className="rounded-xl border border-red-400/30 bg-red-400/10 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-400">Pain Flag Active</p>
            <p className="text-xs text-zinc-400 mt-0.5">
              Location: {todayCheckin.pain_location || 'Not specified'} — Coach has been alerted.
            </p>
          </div>
        </div>
      )}

      {/* Interpretation + Training Focus */}
      <div className="card p-5 space-y-3">
        <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Today's Training Recommendation</p>
        <p className="text-sm text-zinc-300">{todayCheckin.training_adjustment}</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Moon, label: 'Sleep', value: `${todayCheckin.sleep_quality}/5`, sub: `${todayCheckin.sleep_duration}h` },
          { icon: Activity, label: 'Soreness', value: `${todayCheckin.soreness_level}/5`, sub: todayCheckin.soreness_level <= 2 ? 'Low' : todayCheckin.soreness_level <= 3 ? 'Moderate' : 'High' },
          { icon: Brain, label: 'Stress', value: `${todayCheckin.stress_level}/5`, sub: todayCheckin.stress_level <= 2 ? 'Low' : todayCheckin.stress_level <= 3 ? 'Moderate' : 'High' },
          { icon: Flame, label: 'Streak', value: `${streak}`, sub: streak === 1 ? 'day' : 'days' },
        ].map(({ icon: Icon, label, value, sub }) => (
          <div key={label} className="card p-4 flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <Icon size={13} className="text-zinc-500" />
              <p className="text-xs text-zinc-500">{label}</p>
            </div>
            <p className="text-xl font-bold text-zinc-100">{value}</p>
            <p className="text-xs text-zinc-600">{sub}</p>
          </div>
        ))}
      </div>

      {/* Readiness Trend Chart */}
      {chartData.length > 1 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={15} style={{ color: '#E10600' }} />
            <p className="text-sm font-semibold text-zinc-200">Readiness Trend (30 days)</p>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#71717a' }} tickLine={false} axisLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#71717a' }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(225,6,0,0.2)', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#71717a' }}
                itemStyle={{ color: '#E10600' }}
              />
              <ReferenceLine y={85} stroke="#34d399" strokeDasharray="4 4" strokeOpacity={0.4} />
              <ReferenceLine y={70} stroke="#fbbf24" strokeDasharray="4 4" strokeOpacity={0.4} />
              <ReferenceLine y={50} stroke="#fb923c" strokeDasharray="4 4" strokeOpacity={0.4} />
              <Line
                type="monotone"
                dataKey="readiness"
                stroke="#E10600"
                strokeWidth={2}
                dot={{ r: 3, fill: '#E10600', strokeWidth: 0 }}
                activeDot={{ r: 5 }}
                name="Readiness"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2 text-xs text-zinc-600">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-400 inline-block" /> Green ≥85</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-400 inline-block" /> Yellow ≥70</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-orange-400 inline-block" /> Orange ≥50</span>
          </div>
        </div>
      )}

      {/* Sleep & Soreness Trends */}
      {chartData.length > 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: 'sleep', label: 'Sleep Duration (hrs)', color: '#818cf8', domain: [0, 12] as [number, number] },
            { key: 'soreness', label: 'Soreness Level (1-5)', color: '#f87171', domain: [1, 5] as [number, number] },
          ].map(({ key, label, color, domain }) => (
            <div key={key} className="card p-4">
              <p className="text-xs font-medium text-zinc-500 mb-3">{label}</p>
              <ResponsiveContainer width="100%" height={100}>
                <LineChart data={chartData} margin={{ top: 2, right: 2, left: -28, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#52525b' }} tickLine={false} axisLine={false} />
                  <YAxis domain={domain} tick={{ fontSize: 9, fill: '#52525b' }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 11 }}
                    labelStyle={{ color: '#a1a1aa' }}
                  />
                  <Line type="monotone" dataKey={key} stroke={color} strokeWidth={1.5} dot={{ r: 2, fill: color, strokeWidth: 0 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}

      {/* Recent Check-in History */}
      {history.length > 0 && (
        <div className="card p-5">
          <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">Recent Check-Ins</p>
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
            {[...history].reverse().slice(0, 10).map(row => {
              const m = STATUS_META[row.readiness_status as ReadinessStatus];
              return (
                <div key={row.id} className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${m.dotClass}`} />
                  <span className="text-xs text-zinc-500 w-20 flex-shrink-0">
                    {new Date(row.checkin_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <span className="text-sm font-semibold" style={{ color: m.color }}>{row.readiness_score}</span>
                  <span className="text-xs" style={{ color: m.color }}>{m.label}</span>
                  {row.body_pain && <AlertTriangle size={11} className="text-red-400 ml-auto" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
